import React, { useMemo, useEffect, useState } from 'react';
import ptaeService from '../auditor/service/ptaeService.mjs';

/**
 * Picks a PTAE by walking the chart of accounts: project → task → expense.
 *
 * Three steps rather than four: the award is fully determined by project+task in
 * the source data (17 combinations, none with more than one award), so asking
 * for it would be a question with a single possible answer. It is displayed once
 * the task is chosen, so the full code is still visible and checkable.
 *
 * The expense step ALWAYS shows the description next to the code. A task can
 * carry up to 48 expense codes, and "54830" means nothing to a traveller -
 * "54830 · OFF CAMPUS WIRELESS PHONE CHGS" is what they recognise.
 */

/** Shared loader so several pickers on one page don't each refetch the tree. */
export function usePtaeOptions(region = '') {
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    ptaeService.getOptions(region)
      .then(data => { if (!cancelled) setOptions(data); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [region]);

  return { options, error };
}

/**
 * code -> { description, fundName } for every code in the chart, so a code
 * shown after the picker has closed - a bucket, a saved allocation - keeps its
 * description. A bucket showed the bare code once it was added (2026-10-21),
 * and "54830" means nothing on its own.
 */
export const ptaeIndexOf = (options) => {
  const index = new Map();
  for (const p of options?.projects || []) {
    for (const t of p.tasks || []) {
      for (const e of t.expenses || []) index.set(e.code, { description: e.description, fundName: e.fundName });
    }
  }
  for (const u of options?.unstructured || []) {
    if (!index.has(u.code)) index.set(u.code, { description: u.description, fundName: u.fundName });
  }
  return index;
};

/** "code - description" as plain text, for a <select> option or a title. */
export const ptaeText = (code, index) => {
  const description = index?.get(code)?.description;
  return description ? `${code} - ${description}` : (code || '');
};

/** A PTAE code with its description beside it. The code alone when the chart has no description for it. */
export const PtaeCodeLabel = ({ code, index, className = '' }) => {
  const description = index?.get(code)?.description;
  return (
    <span className={`min-w-0 break-words ${className}`}>
      <span className="font-mono break-all">{code}</span>
      {description && <span className="font-sans font-normal text-gray-600"> - {description}</span>}
    </span>
  );
};

const selectClass =
  'w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm ' +
  'focus:ring-2 focus:ring-stanford-red focus:border-transparent ' +
  'disabled:bg-gray-100 disabled:cursor-not-allowed';

const PtaeCascadePicker = ({
  value = '',
  onChange,
  options,
  disabled = false,
  compact = false,
}) => {
  // Derive the three steps from the currently selected code, so the picker
  // reflects an allocation loaded from the server without separate state to
  // keep in sync (which is how the previous two-stage selector drifted).
  const selected = useMemo(() => {
    if (!value || !options) return { project: '', task: '', expense: '' };
    for (const p of options.projects || []) {
      for (const t of p.tasks || []) {
        const hit = (t.expenses || []).find(e => e.code === value);
        if (hit) return { project: p.project, task: t.task, expense: hit.expense };
      }
    }
    return { project: '', task: '', expense: '' };
  }, [value, options]);

  const [draft, setDraft] = useState(selected);
  useEffect(() => { setDraft(selected); }, [selected]);

  const projects = options?.projects || [];
  const tasks = projects.find(p => p.project === draft.project)?.tasks || [];
  const activeTask = tasks.find(t => t.task === draft.task);
  const expenses = activeTask?.expenses || [];

  const pickProject = (project) => {
    setDraft({ project, task: '', expense: '' });
    if (value) onChange('');
  };

  const pickTask = (task) => {
    setDraft({ ...draft, task, expense: '' });
    if (value) onChange('');
  };

  const pickExpense = (expenseCode) => {
    setDraft({ ...draft, expense: expenseCode });
    const hit = expenses.find(e => e.expense === expenseCode);
    onChange(hit ? hit.code : '');
  };

  // Codes predating the project/task/expense split have no parts. They are NOT
  // selectable: picking one bypasses project -> task -> expense, which is the
  // whole point of this picker, and an unparsed chart string is exactly the kind
  // of code nobody can check. They are still resolved here so a claim already
  // funded by one keeps showing its description instead of a bare number.
  const unstructured = options?.unstructured || [];
  const valueIsLegacy = !!value && unstructured.some(u => u.code === value);

  if (!options) {
    return <p className="text-sm text-gray-500">Loading chart of accounts…</p>;
  }

  if (projects.length === 0) {
    return (
      <p className="text-sm text-amber-700">
        No approved PTAE codes with project/task detail are available for this
        region yet.
        {unstructured.length > 0 && (
          <> {unstructured.length} older code{unstructured.length === 1 ? '' : 's'} exist
          without project/task detail and can no longer be selected - re-import the
          chart of accounts to make them usable.</>
        )}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className={compact ? 'space-y-2' : 'grid gap-2 sm:grid-cols-3'}>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
            Project
          </label>
          <select
            value={draft.project}
            onChange={(e) => pickProject(e.target.value)}
            disabled={disabled}
            className={selectClass}
          >
            <option value="">Select project…</option>
            {projects.map(p => (
              <option key={p.project} value={p.project}>{p.project}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
            Task
          </label>
          <select
            value={draft.task}
            onChange={(e) => pickTask(e.target.value)}
            disabled={disabled || !draft.project}
            className={selectClass}
          >
            <option value="">{draft.project ? 'Select task…' : 'Pick a project first'}</option>
            {tasks.map(t => (
              <option key={t.task} value={t.task}>
                {t.task}{t.award ? ` · award ${t.award}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
            Expense
          </label>
          <select
            value={draft.expense}
            onChange={(e) => pickExpense(e.target.value)}
            disabled={disabled || !draft.task}
            className={selectClass}
          >
            <option value="">
              {draft.task ? `Select expense… (${expenses.length})` : 'Pick a task first'}
            </option>
            {expenses.map(e => (
              // Description always shown - the bare code is not identifiable.
              <option key={e.code} value={e.expense}>
                {e.expense} · {e.description}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* The resolved code and its funding programme, so the choice is checkable
          rather than something the user has to trust. */}
      {value && (
        <div className="text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 break-words">
          {(() => {
            const hit = expenses.find(e => e.code === value)
              || unstructured.find(u => u.code === value);
            return (
              <>
                {hit?.fundName && (
                  <span className="text-gray-500">({hit.fundName}) </span>
                )}
                <code className="font-mono text-stanford-red">{value}</code>
                {hit?.description && (
                  <span className="text-gray-700"> - {hit.description}</span>
                )}
                {valueIsLegacy && (
                  <span className="block mt-1 text-amber-700">
                    Older code with no project/task detail. It stays on this claim,
                    but changing the selection below cannot bring it back.
                  </span>
                )}
              </>
            );
          })()}
        </div>
      )}

    </div>
  );
};

export default PtaeCascadePicker;
